import type { CompletionStatus } from '~/helpers/learner/session-view'
import { useEffect, useRef } from 'react'
import { Badge } from '~/components/ui/badge'
import { cn } from '~/lib/utils'
import HighlightedText from '~/pages/scorm/components/learner-search/HighlightedText'
import { useStatusMeta } from '~/pages/scorm/components/useStatusMeta'

type IProps = {
  id: string
  active: boolean
  title: string
  query: string
  /** Ce qui situe le résultat : son type, ou le parcours qui le contient. */
  context: React.ReactNode
  status: CompletionStatus
  /** Avancement d'une formation, ou verrou d'un module. */
  detail?: string
  onSelect: () => void
  onHover: () => void
}

/**
 * Une option de la recherche apprenant (#109). Le focus reste dans le champ,
 * qui porte `aria-activedescendant` ; le panneau empêche donc qu'un clic le lui
 * vole. L'option active est ramenée dans la partie visible du panneau, qui
 * défile : les flèches désigneraient sinon une option hors de l'écran.
 */
function SearchResultItem({ id, active, title, query, context, status, detail, onSelect, onHover }: IProps) {
  const meta = useStatusMeta()(status)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // `scrollIntoView` manque à jsdom : appel conditionnel.
    if (active) ref.current?.scrollIntoView?.({ block: 'nearest' })
  }, [active])

  return (
    // Motif combobox ARIA : le clavier (flèches, Entrée) est porté par le champ
    // via `aria-activedescendant`, pas par chaque option ; le clic est l'appoint souris.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      ref={ref}
      id={id}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onMouseMove={onHover}
      onClick={onSelect}
      className={cn('flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2', active && 'bg-muted')}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium"><HighlightedText text={title} query={query} /></p>
        <p className="truncate text-xs text-muted-foreground">{context}</p>
      </div>
      {detail && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{detail}</span>}
      <Badge variant={meta.badgeVariant} className="shrink-0">{meta.badgeLabel}</Badge>
    </div>
  )
}

export default SearchResultItem
